import { Injectable, inject } from '@angular/core';
import { Firestore, collection, addDoc, collectionData, doc, updateDoc, query, where, deleteDoc } from '@angular/fire/firestore';
import { Observable, from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Promotion } from './promotion';
import { CurrentUserService } from '../../shared/services/current-user.service';

const PROMOTIONS_COLLECTION = 'promotions';

/** Fields stored for a promotion; the company is added by the service on create. */
function toDocument({ size, discountPrice, startDate, endDate }: Promotion) {
  return { size, discountPrice, startDate, endDate };
}

@Injectable({
  providedIn: 'root'
})
export class PromotionService {
  readonly #firestore = inject(Firestore);
  readonly #currentUser = inject(CurrentUserService);

  #collection() {
    return collection(this.#firestore, PROMOTIONS_COLLECTION);
  }

  #docRef(id: string) {
    return doc(this.#firestore, PROMOTIONS_COLLECTION, id);
  }

  /** Creates a promotion in the current user's company. */
  storePromotion(promotion: Promotion): Observable<string> {
    return from((async () => {
      const company = await this.#currentUser.requireCompany();
      const docRef = await addDoc(this.#collection(), { ...toDocument(promotion), ...company });
      return docRef.id;
    })());
  }

  updatePromotion(promotion: Promotion): Observable<string> {
    const { id } = promotion;
    if (!id) {
      throw new Error('Promotion ID is required for update');
    }

    return from(updateDoc(this.#docRef(id), toDocument(promotion)).then(() => id));
  }

  deletePromotion(promotionId: string): Observable<void> {
    return from(deleteDoc(this.#docRef(promotionId)));
  }

  /** Live list of the current user's company promotions that haven't ended yet. */
  getActivePromotions(): Observable<Promotion[]> {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    return this.#currentUser.company$.pipe(
      switchMap(company => {
        if (!company) return of([]);

        const activePromotionsQuery = query(
          this.#collection(),
          where('companyId', '==', company.companyId),
          where('endDate', '>=', today)
        );
        return collectionData(activePromotionsQuery, { idField: 'id' }) as Observable<Promotion[]>;
      })
    );
  }
}
